import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CalendarDate, today, getLocalTimeZone, DateValue } from '@internationalized/date';
import { Typography, Button, DatePickerCustom } from '../../../../components';
import { icons } from '../../../../icons';
import { HeadLine3 } from '../../../../components/typography/styled';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  min-width: 400px;
  max-width: 500px;
  background: ${({theme}) => theme.palette[10]};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const BodyModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
`;

interface ServiceEnableBillingModalProps {
  service: any;
  onEnable: (newCycleStart: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ServiceEnableBillingModal: FC<ServiceEnableBillingModalProps> = ({
  service,
  onEnable,
  onCancel,
  loading = false,
}) => {
  const [t] = useTranslation();
  const [newCycleStart, setNewCycleStart] = useState<DateValue | null>(today(getLocalTimeZone()));

  const handleDateChange = (value: DateValue | null) => {
    setNewCycleStart(value);
  };

  const handleEnable = () => {
    if (!newCycleStart) return;

    const dateStr = `${newCycleStart.year}-${String(newCycleStart.month).padStart(2, '0')}-${String(newCycleStart.day).padStart(2, '0')}`;
    onEnable(dateStr);
  };

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
          {t('ENABLE_BILLING')}
        </HeadLine3>
        <Icon onClick={onCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Typography variant="body">
          {t('ENABLE_BILLING_DESC', { state: service.last_service_cycle_state })}
        </Typography>

        <DatePickerCustom
          title={String(t('NEW_CYCLE_START'))}
          value={newCycleStart}
          onChange={handleDateChange}
        />

        <ButtonsWrapper>
          <Button
            variant="stroke"
            size="display"
            onClick={onCancel}
            disabled={loading}
          >
            {t('CANCEL')}
          </Button>
          <Button
            variant="primary"
            size="display"
            onClick={handleEnable}
            loading={loading}
            disabled={!newCycleStart}
          >
            {t('ENABLE_BILLING')}
          </Button>
        </ButtonsWrapper>
      </BodyModal>
    </Container>
  );
};

export default ServiceEnableBillingModal;
